import { useCallback, useEffect, useRef, useState } from 'react';

import type { DocNode } from '@atlaskit/adf-schema/doc';
import { fg } from '@atlaskit/platform-feature-flags';
import { usePublish, useSubscribe } from '@atlaskit/rovo-triggers/main';
import type {
	TaskAskQuestionConfirmedPayload,
	TaskAskQuestionRenderedPayload,
	TaskModifyPlanRequestedPayload,
	TaskPlanConfirmedPayload,
	TaskPlanRenderedPayload,
} from '@atlaskit/rovo-triggers/types';
import { useChatModeState } from '@atlassian/conversation-assistant-chat-prompt-input/useChatModeState';
import type {
	ExecutionSegment,
	TaskProgressState,
	TodoSnapshot,
} from '@atlassian/conversation-assistant-store/controllers/chat/types';
import { useChatMessages } from '@atlassian/conversation-assistant-store/controllers/chat/use-chat-messages';
import {
	useActiveTaskForConversation,
	useTasksStoreActions as useUnifiedTasksStoreActions,
} from '@atlassian/conversation-assistant-store/controllers/unified-tasks';

import { extractPromptTextFromAdf } from '../../common/utils/content-features/extractPromptTextFromAdf';
import { useChatStore } from '../chat-store/useChatStore';
import { useChatStoreActions } from '../chat-store/useChatStoreActions';

// ── Screen discriminated union ──────────────────────────────────────────

export type TaskScreen =
	| {
			type: 'executing';
			todoSnapshot?: TodoSnapshot;
			taskTitle?: string;
			executionSegments?: ExecutionSegment[] | null;
			isPaused?: boolean;
	  }
	| { type: 'awaiting-disambiguation'; invocationId: string; taskTitle?: string }
	| {
			type: 'awaiting-plan-confirmation';
			invocationId: string;
			isModifying: boolean;
			taskTitle?: string;
	  }
	| { type: 'completed'; taskTitle?: string }
	| { type: 'none' };

export type UseTaskProgressActiveResult = {
	screen: TaskScreen;
	handleStopTask: () => void;
	handleDoneClicked: () => void;
	onSubmitOverride: (prompt: DocNode) => void;
};

/**
 * Hook that owns all task-progress state derivation and event handling.
 *
 * Returns a discriminated `screen` union so the consumer (ChatInputOverlay)
 * can render the correct view without any state logic of its own.
 */
export const useTaskProgressActive = (): UseTaskProgressActiveResult => {
	const [{ currentConversationId }] = useChatStore();
	const { stopTask } = useChatStoreActions();
	const chatMode = useChatModeState(currentConversationId);
	const [messages] = useChatMessages({ conversationId: currentConversationId });
	const publish = usePublish('ai-mate-actions');

	const [lastTask] = useActiveTaskForConversation(currentConversationId);
	const todoSnapshot = lastTask?.todoSnapshot;
	const executionSegments = lastTask?.executionSegments;

	// eslint-disable-next-line @atlaskit/platform/no-preconditioning -- enable_tasks is the umbrella gate for task updates.
	const isTasksEnabled = fg('rovo_chat_enable_tasks');
	const unifiedTasksActions = useUnifiedTasksStoreActions();

	const dispatchTaskProgressUpdate = useCallback(
		(args: {
			conversationId: string;
			progressState: TaskProgressState;
			title?: string;
			updatedAt?: string;
			executionSegments?: ExecutionSegment[];
		}) => {
			if (!chatMode.taskModeEnabled) {
				return;
			}

			unifiedTasksActions.setTaskProgressState({
				conversationId: args.conversationId,
				progressState: args.progressState,
				title: args.title,
				updatedAt: args.updatedAt,
				executionSegments: args.executionSegments,
			});
		},
		[chatMode.taskModeEnabled, unifiedTasksActions],
	);

	// ── Local UI state ──────────────────────────────────────────────────
	const activeInvocationIdRef = useRef<string | undefined>(undefined);
	const modifyInvocationIdRef = useRef<string | undefined>(undefined);
	const [isModifyingPlan, setIsModifyingPlan] = useState(false);
	const [showCompletedView, setShowCompletedView] = useState(false);
	const messageCountAtCompletionRef = useRef<number | undefined>(undefined);

	// Reset completed view when conversation changes
	useEffect(() => {
		if (!isTasksEnabled) {
			return;
		}
		setShowCompletedView(false);
		messageCountAtCompletionRef.current = undefined;
	}, [currentConversationId, isTasksEnabled]);

	// ── Pubsub event handling ───────────────────────────────────────────
	useSubscribe({ topic: 'ai-mate-chat-input-overlay', triggerLatest: true }, (payload) => {
		if (!isTasksEnabled) {
			return;
		}

		if (payload.type === 'task-plan-confirmed') {
			const { conversationId, planTitle } = (payload as TaskPlanConfirmedPayload).data;
			activeInvocationIdRef.current = undefined;
			modifyInvocationIdRef.current = undefined;
			setIsModifyingPlan(false);
			const now = new Date().toISOString();
			dispatchTaskProgressUpdate({
				conversationId,
				progressState: 'Running',
				title: planTitle,
				updatedAt: now,
				executionSegments: [{ startedAt: now }],
			});
		}

		if (payload.type === 'task-ask-question-rendered') {
			const { conversationId, invocationId } = (payload as TaskAskQuestionRenderedPayload).data;
			activeInvocationIdRef.current = invocationId;
			dispatchTaskProgressUpdate({
				conversationId,
				progressState: 'AwaitingDisambiguation',
				updatedAt: new Date().toISOString(),
			});
		}

		if (payload.type === 'task-ask-question-confirmed') {
			const { conversationId } = (payload as TaskAskQuestionConfirmedPayload).data;
			activeInvocationIdRef.current = undefined;
			dispatchTaskProgressUpdate({
				conversationId,
				progressState: 'Draft',
				updatedAt: new Date().toISOString(),
			});
		}

		if (payload.type === 'task-plan-rendered') {
			const { conversationId, invocationId } = (payload as TaskPlanRenderedPayload).data;
			activeInvocationIdRef.current = invocationId;
			modifyInvocationIdRef.current = undefined;
			setIsModifyingPlan(false);
			dispatchTaskProgressUpdate({
				conversationId,
				progressState: 'AwaitingPlanConfirmation',
				updatedAt: new Date().toISOString(),
			});
		}

		if (payload.type === 'task-modify-plan-requested') {
			const { conversationId, invocationId } = (payload as TaskModifyPlanRequestedPayload).data;
			if (conversationId === currentConversationId) {
				modifyInvocationIdRef.current = invocationId;
				setIsModifyingPlan(true);
			}
		}
	});

	// ── Completed‑view effects ──────────────────────────────────────────
	useEffect(() => {
		if (!isTasksEnabled) {
			return;
		}
		if (lastTask?.progressState === 'Completed' && lastTask?.seekingCompletedAck) {
			setShowCompletedView(true);
			messageCountAtCompletionRef.current = messages?.length;
			const taskId = lastTask?.taskId;
			if (taskId) {
				unifiedTasksActions.ackTaskCompleted({ taskId });
			}
		}
	}, [
		lastTask?.progressState,
		lastTask?.seekingCompletedAck,
		lastTask?.taskId,
		messages?.length,
		isTasksEnabled,
		unifiedTasksActions,
	]);

	useEffect(() => {
		if (!isTasksEnabled) {
			return;
		}
		if (
			showCompletedView &&
			messageCountAtCompletionRef.current !== undefined &&
			messages &&
			messages.length > messageCountAtCompletionRef.current
		) {
			setShowCompletedView(false);
			messageCountAtCompletionRef.current = undefined;
		}
	}, [showCompletedView, messages, isTasksEnabled]);

	// ── Callbacks ───────────────────────────────────────────────────────
	const handleStopTask = useCallback(() => {
		stopTask({ conversationId: currentConversationId });
	}, [stopTask, currentConversationId]);

	const handleDoneClicked = useCallback(() => {
		setShowCompletedView(false);
		messageCountAtCompletionRef.current = undefined;
	}, []);

	const onSubmitOverride = useCallback(
		(prompt: DocNode) => {
			const promptText = extractPromptTextFromAdf(prompt);
			if (promptText.trim() && modifyInvocationIdRef.current) {
				publish({
					type: 'task-modify-plan-submitted',
					source: 'chatInputOverlay',
					data: {
						conversationId: currentConversationId,
						invocationId: modifyInvocationIdRef.current,
						prompt,
					},
				});
				dispatchTaskProgressUpdate({
					conversationId: currentConversationId,
					progressState: 'Draft',
				});
			}
			modifyInvocationIdRef.current = undefined;
			setIsModifyingPlan(false);
		},
		[currentConversationId, publish, dispatchTaskProgressUpdate],
	);

	// ── Derive screen ───────────────────────────────────────────────────
	const progressState = lastTask?.progressState;
	const taskTitle = lastTask?.title;

	const noneResult: UseTaskProgressActiveResult = {
		screen: { type: 'none' },
		handleStopTask,
		handleDoneClicked,
		onSubmitOverride,
	};

	// Completed view is shown even when task mode is no longer active (reasoning
	// mode resets to 'auto' on completion). The one-time display is already
	// guarded by seekingCompletedAck / showCompletedView state.
	if (
		isTasksEnabled &&
		lastTask &&
		((lastTask.progressState === 'Completed' && lastTask.seekingCompletedAck) || showCompletedView)
	) {
		return {
			screen: { type: 'completed', taskTitle },
			handleStopTask,
			handleDoneClicked,
			onSubmitOverride,
		};
	}

	// Gate: tasks must be enabled and task mode active
	if (!isTasksEnabled || !chatMode.taskModeEnabled || !lastTask) {
		return noneResult;
	}

	if (progressState === 'Running' || progressState === 'AwaitingActionConfirmation') {
		return {
			screen: {
				type: 'executing',
				todoSnapshot,
				taskTitle,
				executionSegments,
				isPaused: progressState === 'AwaitingActionConfirmation',
			},
			handleStopTask,
			handleDoneClicked,
			onSubmitOverride,
		};
	}

	if (progressState === 'AwaitingPlanConfirmation' && activeInvocationIdRef.current) {
		return {
			screen: {
				type: 'awaiting-plan-confirmation',
				invocationId: activeInvocationIdRef.current,
				isModifying: isModifyingPlan,
				taskTitle,
			},
			handleStopTask,
			handleDoneClicked,
			onSubmitOverride,
		};
	}

	if (progressState === 'AwaitingDisambiguation' && activeInvocationIdRef.current) {
		return {
			screen: {
				type: 'awaiting-disambiguation',
				invocationId: activeInvocationIdRef.current,
				taskTitle,
			},
			handleStopTask,
			handleDoneClicked,
			onSubmitOverride,
		};
	}

	return noneResult;
};
